import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Tab,
  Tabs,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  ContentCopy as DuplicateIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 36,
  height: 36,
  borderBottom: "1px solid #e0e0e0",
  backgroundColor: "#e5e5e5",
  paddingLeft: 0,
  paddingRight: 0,
  margin: 0,
  paddingBottom: 0,
  boxShadow: "none",
  borderRadius: "0",
  "& .MuiTabs-indicator": {
    display: "none",
  },
  "& .MuiTabs-scrollButtons": {
    color: theme.palette.primary.main,
    width: 28,
    height: 36,
    "&.Mui-disabled": {
      opacity: 0.3,
    },
  },
  "& .MuiTabs-flexContainer": {
    minHeight: 36,
    height: 36,
    alignItems: "flex-end",
    gap: "4px",
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: 0,
  },
  "& .MuiTabs-scroller": {
    height: 36,
    marginBottom: 0,
  },
}));

const TAB_BAR_HEIGHT = 36;

const StyledTab = styled(Tab, {
  shouldForwardProp: (prop) => !["isActive", "savedQueryId", "textColor"].includes(prop),
})(({ theme, isActive, savedQueryId }) => ({
  "& .MuiButtonBase-root": {
    minWidth: 80,
    maxWidth: 180,
    width: "auto",
    height: isActive ? TAB_BAR_HEIGHT : 32,
    minHeight: isActive ? TAB_BAR_HEIGHT : 32,
    padding: "0 16px",
    marginRight: 0,
    marginLeft: 0,
    marginBottom: 0,
    borderRadius: "6px 6px 0 0",
    backgroundColor: isActive ? "#ffffff" : "transparent",
    border: isActive ? "1px solid #e0e0e0" : "none",
    borderBottom: isActive ? "none" : "none",
    color: isActive ? "#3F49A7" : "#666666",
    fontWeight: isActive ? 600 : 500,
    fontSize: 13,
    textTransform: "none",
    position: "relative",
    transition: "all 0.2s ease",
    zIndex: isActive ? 2 : 1,
    overflow: "hidden",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: isActive ? "#ffffff" : "#e8e8e8",
      color: isActive ? "#3F49A7" : "#333333",
      cursor: savedQueryId ? "default" : "pointer",
    },
  },
  minWidth: 80,
  maxWidth: 180,
  width: "auto",
  height: isActive ? TAB_BAR_HEIGHT : 32,
  minHeight: isActive ? TAB_BAR_HEIGHT : 32,
  padding: "0 16px",
  marginRight: 0,
  marginLeft: 0,
  marginBottom: 0,
  borderRadius: "6px 6px 0 0",
  backgroundColor: isActive ? "#ffffff" : "transparent",
  border: isActive ? "1px solid #e0e0e0" : "none",
  borderBottom: isActive ? "none" : "none",
  color: isActive ? "#3F49A7" : "#666666",
  fontWeight: isActive ? 600 : 500,
  fontSize: 13,
  textTransform: "none",
  position: "relative",
  transition: "all 0.2s ease",
  zIndex: isActive ? 2 : 1,
  overflow: "hidden",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: isActive ? "#ffffff" : "#e8e8e8",
    color: isActive ? "#3F49A7" : "#333333",
    cursor: savedQueryId ? "default" : "pointer",
  },
  "&::after": isActive ? {
    content: '""',
    position: "absolute",
    bottom: -1,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: "#3F49A7",
    borderRadius: "0",
  } : {},
  "& .MuiTab-wrapper": {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    color: "inherit",
    overflow: "hidden",
    minHeight: isActive ? TAB_BAR_HEIGHT : 32,
    height: isActive ? TAB_BAR_HEIGHT : 32,
  },
  "& > .MuiBox-root": {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    minHeight: isActive ? TAB_BAR_HEIGHT : 32,
    height: isActive ? TAB_BAR_HEIGHT : 32,
    overflow: "hidden",
  },
  "& .MuiTab-labelIcon": {
    color: "inherit",
  },
}));

const TabLabel = styled(Typography)({
  flex: 1,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontSize: "inherit",
  fontWeight: "inherit",
  color: "inherit",
});

const TabActions = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginLeft: 2,
  opacity: 1,
  transition: "opacity 0.2s ease",
});

const AddTabButton = styled(Box)(({ theme }) => ({
  minWidth: 32,
  maxWidth: 32,
  width: 32,
  height: 32,
  padding: 0,
  marginLeft: 4,
  marginBottom: 0,
  borderRadius: "6px 6px 0 0",
  backgroundColor: "transparent",
  border: "none",
  color: "#3F49A7",
  fontWeight: 700,
  fontSize: 12,
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#e8e8e8",
  },
  "&:active": {
    backgroundColor: "#d8d8d8",
  },
  "&:disabled": {
    opacity: 0.4,
    color: "#999",
  },
}));

const ContextMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 4,
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
    minWidth: 100,
    maxWidth: 150,
    padding: "2px 0",
  },
}));

const ContextMenuItem = styled(MenuItem)(({ theme, danger }) => ({
  fontSize: 10,
  padding: "2px 6px",
  minHeight: 20,
  color: danger ? theme.palette.error.main : theme.palette.text.primary,
  "&:hover": {
    backgroundColor: danger
      ? theme.palette.error.light
      : theme.palette.action.hover,
    color: danger ? theme.palette.error.dark : theme.palette.text.primary,
  },
  "& .MuiSvgIcon-root": {
    fontSize: 10,
    marginRight: 3,
  },
}));

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ExcelSheetTabs = ({
  sheets = [],
  activeSheetIndex = 0,
  onSheetChange,
  onSheetAdd,
  onSheetRename,
  onSheetDelete,
  onSheetDuplicate,
  onSheetReorder,
  maxSheets = 50,
  allowRename = true,
  allowDelete = true,
  allowDuplicate = true,
  allowReorder = true,
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [contextMenu, setContextMenu] = useState(null);
  const [editingTab, setEditingTab] = useState({
    isEditing: false,
    sheetIndex: -1,
    tempName: "",
  });
  const [dragState, setDragState] = useState({
    isDragging: false,
    dragIndex: -1,
  });
  const [hoveredTab, setHoveredTab] = useState(-1);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const inputRef = useRef(null);
  const tabsWrapperRef = useRef(null);
  const scrollAmount = 180;

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleTabChange = (event, newValue) => {
    if (newValue !== activeSheetIndex && onSheetChange) {
      onSheetChange(newValue);
    }
  };

  const handleAddSheet = () => {
    if (sheets.length >= maxSheets) {
      alert(`Maximum ${maxSheets} sheets allowed`);
      return;
    }
    if (onSheetAdd) {
      // Pass 0 as the index to add the sheet at the beginning
      onSheetAdd(0);
    }
  };

  const handleContextMenu = (event, sheetIndex) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      sheetIndex,
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleDoubleClick = (sheetIndex) => {
    // Don't allow renaming if it's a saved query tab
    if (allowRename && !sheets[sheetIndex]?.saved_query_id) {
      const currentSheet = sheets[sheetIndex];
      const currentName = currentSheet?.name || `Task-${sheetIndex + 1}`;
      setEditingTab({
        isEditing: true,
        sheetIndex,
        tempName: currentName,
      });
    }
  };

  const handleRenameConfirm = () => {
    const { sheetIndex, tempName } = editingTab;
    const trimmedName = tempName.trim();
    
    // Only rename if we have a valid name and it's different from the current name
    if (trimmedName && onSheetRename && sheetIndex >= 0 && sheetIndex < sheets.length) {
      const currentSheet = sheets[sheetIndex];
      const currentName = currentSheet?.name || `Task-${sheetIndex + 1}`;
      
      if (trimmedName !== currentName) {
        onSheetRename(sheetIndex, trimmedName);
      }
    }
    
    setEditingTab({ isEditing: false, sheetIndex: -1, tempName: "" });
  };

  const handleRenameCancel = () => {
    setEditingTab({ isEditing: false, sheetIndex: -1, tempName: "" });
  };

  const handleRenameKeyPress = (e) => {
    if (e.key === "Enter") {
      handleRenameConfirm();
    } else if (e.key === "Escape") {
      handleRenameCancel();
    }
  };

  const handleDeleteClick = (sheetIndex) => {
    if (allowDelete && sheets.length > 1 && onSheetDelete) {
      onSheetDelete(sheetIndex);
    }
  };

  const handleDuplicateClick = () => {
    const sheetIndex = contextMenu?.sheetIndex;
    if (sheetIndex !== undefined && allowDuplicate && onSheetDuplicate) {
      onSheetDuplicate(sheetIndex);
    }
    handleContextMenuClose();
  };

  const handleDragStart = (event, index) => {
    if (!allowReorder) {
      console.log('🚫 Reordering not allowed');
      return;
    }
    
    console.log('🔄 Starting drag:', { 
      index,
      sheetName: sheets[index]?.name,
      sheetId: sheets[index]?.id,
      activeIndex: activeSheetIndex,
      totalSheets: sheets.length
    });
    
    // Set drag state and visual feedback
    setDragState({ isDragging: true, dragIndex: index });
    
    // Set drag data
    event.dataTransfer.effectAllowed = "move";
    try {
      const dragData = {
        dragIndex: index,
        sheetId: sheets[index]?.id || index,
        sheetName: sheets[index]?.name
      };
      event.dataTransfer.setData("text/plain", JSON.stringify(dragData));
      console.log('📤 Set drag data:', dragData);
    } catch (error) {
      console.error('❌ Error setting drag data:', error);
    }
    
    // Add visual feedback
    const draggedTab = event.target;
    if (draggedTab) {
      draggedTab.style.opacity = '0.5';
    }
  };

  const handleDragOver = (event, hoverIndex) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Only handle if we're dragging a tab
    if (!dragState.isDragging) return;
    
    // Set the drop effect to move
    event.dataTransfer.dropEffect = "move";
    
    // Add visual feedback for the drop target
    const dropTarget = event.currentTarget;
    if (dropTarget) {
      dropTarget.style.borderLeft = hoverIndex > dragState.dragIndex ? '2px solid #3F49A7' : '';
      dropTarget.style.borderRight = hoverIndex <= dragState.dragIndex ? '2px solid #3F49A7' : '';
    }
  };

  const handleDragLeave = (event) => {
    // Remove drop target visual feedback
    const dropTarget = event.currentTarget;
    if (dropTarget) {
      dropTarget.style.borderLeft = '';
      dropTarget.style.borderRight = '';
    }
  };

  const handleDrop = (event, dropIndex) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (!allowReorder) {
      console.log('🚫 Drop not allowed - reordering disabled');
      return;
    }

    console.log('📥 Drop event:', { 
      dropIndex,
      dropTargetName: sheets[dropIndex]?.name,
      activeIndex: activeSheetIndex
    });

    try {
      // Get the drag data
      const dragDataStr = event.dataTransfer.getData("text/plain");
      console.log('📦 Retrieved drag data:', dragDataStr);
      
      const dragData = JSON.parse(dragDataStr);
      const { dragIndex, sheetName } = dragData;
      
      console.log('🔄 Processing drop:', { 
        dragIndex, 
        dragSheetName: sheetName,
        dropIndex,
        dropSheetName: sheets[dropIndex]?.name,
        activeIndex: activeSheetIndex,
        totalSheets: sheets.length
      });

      // Only reorder if indices are different and valid
      if (dragIndex !== dropIndex && 
          dragIndex >= 0 && dragIndex < sheets.length && 
          dropIndex >= 0 && dropIndex < sheets.length) {
        
        if (onSheetReorder) {
          console.log('✨ Calling reorderSheets:', { 
            from: dragIndex, 
            fromName: sheets[dragIndex]?.name,
            to: dropIndex,
            toName: sheets[dropIndex]?.name
          });
          
          // Call the reorder handler
          onSheetReorder(dragIndex, dropIndex);
          
          console.log('✅ Reorder handler called');
        } else {
          console.warn('⚠️ onSheetReorder handler not provided');
        }
      } else {
        console.log('❌ Invalid reorder operation:', { 
          dragIndex, 
          dropIndex, 
          sheetsLength: sheets.length,
          reason: dragIndex === dropIndex ? 'Same position' : 'Invalid index'
        });
      }
    } catch (error) {
      console.error('❌ Error during drop:', error);
    }

    // Reset drag state
    setDragState({ isDragging: false, dragIndex: -1 });
    
    // Remove any visual feedback
    const dropTarget = event.currentTarget;
    if (dropTarget) {
      dropTarget.style.borderLeft = '';
      dropTarget.style.borderRight = '';
    }
  };

  const handleDragEnd = (event) => {
    // Reset drag state
    setDragState({ isDragging: false, dragIndex: -1 });
    
    // Remove visual feedback from the dragged item
    const draggedTab = event.target;
    if (draggedTab) {
      draggedTab.style.opacity = '';
    }
    
    // Remove visual feedback from all potential drop targets
    const allTabs = document.querySelectorAll('[role="tab"]');
    allTabs.forEach(tab => {
      tab.style.borderLeft = '';
      tab.style.borderRight = '';
    });
  };

  // ============================================================================
  // SCROLL ARROWS
  // ============================================================================

  const getScroller = () =>
    tabsWrapperRef.current?.querySelector?.(".MuiTabs-scroller");

  const updateScrollState = React.useCallback(() => {
    const scroller = getScroller();
    if (!scroller) return;
    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const handleScrollLeft = () => {
    const scroller = getScroller();
    if (!scroller) return;
    scroller.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  };

  const handleScrollRight = () => {
    const scroller = getScroller();
    if (!scroller) return;
    scroller.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    const scroller = getScroller();
    if (!scroller) return;
    updateScrollState();
    scroller.addEventListener("scroll", updateScrollState);
    const resizeObs = new ResizeObserver(updateScrollState);
    resizeObs.observe(scroller);
    return () => {
      scroller.removeEventListener("scroll", updateScrollState);
      resizeObs.disconnect();
    };
  }, [sheets.length, updateScrollState]);

  useEffect(() => {
    if (editingTab.isEditing && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
        inputRef.current.select();
      }, 0);
    }
  }, [editingTab.isEditing]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case "t":
            event.preventDefault();
            handleAddSheet();
            break;
          case "w":
            if (allowDelete && sheets.length > 1) {
              event.preventDefault();
              handleDeleteClick(activeSheetIndex);
            }
            break;
          case "d":
            if (allowDuplicate) {
              event.preventDefault();
              handleDuplicateClick();
            }
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [sheets.length, allowDelete, allowDuplicate, activeSheetIndex]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderTabContent = (sheet, index) => {
    // Get the current sheet name, with fallback to Task-N
    const getSheetName = () => {
      if (!sheet) return `Task-${index + 1}`;
      if (sheet.name) return sheet.name;
      if (sheet.saved_query_id) return `Saved Query ${index + 1}`;
      return `Task-${index + 1}`;
    };

    // If editing this tab, show input
    if (editingTab.isEditing && editingTab.sheetIndex === index) {
      return (
        <input
          ref={inputRef}
          value={editingTab.tempName}
          onChange={(e) => {
            const value = e.target.value;
            if (value.length <= 50) {
              setEditingTab((prev) => ({ ...prev, tempName: value }));
            }
          }}
          onBlur={handleRenameConfirm}
          onKeyDown={handleRenameKeyPress}
          maxLength={50}
          style={{
            fontSize: "12px",
            color: "#1a1a1a",
            fontWeight: 600,
            padding: "0 4px",
            textAlign: "center",
            border: "none",
            outline: "none",
            background: "transparent",
            width: "100%",
            maxWidth: "130px",
            minWidth: "50px",
          }}
          autoFocus
        />
      );
    }

    const displayName = getSheetName();

    return (
      <>
        <Tooltip 
          title={displayName}
          placement="top"
          arrow
          enterDelay={500}
          leaveDelay={200}
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: '#333',
                color: '#fff',
                fontSize: '10px',
                fontWeight: 500,
                padding: '4px 8px',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                maxWidth: '250px',
                wordBreak: 'break-word',
                textAlign: 'center',
                '& .MuiTooltip-arrow': {
                  color: '#333',
                },
              },
            },
          }}
        >
          <TabLabel 
            sx={{
              fontSize: "12px",
              color: index === activeSheetIndex ? "#1a1a1a" : "#666666",
              fontWeight: index === activeSheetIndex ? 600 : 400,
              opacity: sheet?.saved_query_id ? 0.8 : 1,
              fontStyle: sheet?.saved_query_id ? "italic" : "normal",
            }}
          >
            {displayName}
          </TabLabel>
        </Tooltip>
        {allowDelete && sheets.length > 1 && (
          <TabActions>
            <Box 
              component="span" 
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(index);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: "2px",
                color: "#888888",
                minWidth: "16px",
                minHeight: "16px",
                ml: 0.25,
                borderRadius: '50%',
                cursor: 'pointer',
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: "rgba(239, 68, 68, 0.12)",
                  color: "#ef4444",
                  transform: "scale(1.1)",
                },
              }}
            >
              <Tooltip title="Delete sheet">
                <CloseIcon sx={{ fontSize: 8 }} />
              </Tooltip>
            </Box>
          </TabActions>
        )}
      </>
    );
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Box
      ref={tabsWrapperRef}
      sx={{
        width: "100%",
        position: "relative",
        m: 0,
        p: 0,
        display: "flex",
        alignItems: "stretch",
        borderBottom: "1px solid #e0e0e0",
        backgroundColor: "#e5e5e5",
      }}
    >
      <IconButton
        size="small"
        onClick={handleScrollLeft}
        disabled={!canScrollLeft}
        aria-label="Scroll tabs left"
        sx={{
          flexShrink: 0,
          width: 28,
          height: 36,
          borderRadius: 0,
          color: "primary.main",
          "&.Mui-disabled": { opacity: 0.3 },
          "&:hover:not(.Mui-disabled)": { backgroundColor: "rgba(0,0,0,0.04)" },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <AddTabButton
        component="div"
        role="button"
        aria-label="Add Sheet"
        onClick={handleAddSheet}
        sx={{
          flexShrink: 0,
          opacity: sheets.length >= maxSheets ? 0.4 : 1,
          pointerEvents: sheets.length >= maxSheets ? "none" : "auto",
          cursor: sheets.length >= maxSheets ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AddIcon sx={{ fontSize: 14 }} />
      </AddTabButton>
      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <StyledTabs
          value={activeSheetIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{ minHeight: 36 }}
          TabIndicatorProps={{
            style: {
              backgroundColor: "#3F49A7",
              height: 2,
              borderRadius: "1px 1px 0 0",
            },
          }}
        >
        {sheets.map((sheet, index) => {
          const tabId = `tab-${index}`;
          return (
            <StyledTab
              key={tabId}
              id={tabId}
              value={index}
              isActive={index === activeSheetIndex}
              savedQueryId={sheet?.saved_query_id || null}
              onContextMenu={(e) => handleContextMenu(e, index)}
              onMouseEnter={() => setHoveredTab(index)}
              onMouseLeave={() => setHoveredTab(-1)}
              onDoubleClick={() => handleDoubleClick(index)}
              draggable={allowReorder && !editingTab.isEditing}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              component="div"
              sx={{
                opacity:
                  dragState.isDragging && dragState.dragIndex === index ? 0.5 : 1,
                transform:
                  dragState.isDragging && dragState.dragIndex === index
                    ? "rotate(2deg)"
                    : "none",
                transition: "all 0.2s ease",
                position: "relative",
                "&::before": dragState.isDragging && dragState.dragIndex !== index ? {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: dragState.dragIndex > index ? 0 : "auto",
                  right: dragState.dragIndex <= index ? 0 : "auto",
                  width: "2px",
                  backgroundColor: "#3F49A7",
                  opacity: 0.5,
                } : {},
                "&:hover": {
                  backgroundColor: dragState.isDragging ? "#f5f5f5" : "inherit",
                },
              }}
              label={
                <Box
                  component="div"
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    width: "100%",
                    maxWidth: "180px",
                    minWidth: "70px",
                    height: "30px",
                  }}
                >
                  {renderTabContent(sheet, index)}
                </Box>
              }
            />
          );
        })}
        </StyledTabs>
      </Box>
      <IconButton
        size="small"
        onClick={handleScrollRight}
        disabled={!canScrollRight}
        aria-label="Scroll tabs right"
        sx={{
          flexShrink: 0,
          width: 28,
          height: 36,
          borderRadius: 0,
          marginRight:2,
          color: "primary.main",
          "&.Mui-disabled": { opacity: 0.3 },
          "&:hover:not(.Mui-disabled)": { backgroundColor: "rgba(0,0,0,0.04)" },
        }}
      >
        <ChevronRightIcon sx={{ fontSize: 20 }} />
      </IconButton>

      <ContextMenu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {allowDuplicate && (
          <ContextMenuItem onClick={handleDuplicateClick}>
            <DuplicateIcon sx={{ fontSize: 10, mr: 0.5 }} />
            Duplicate
          </ContextMenuItem>
        )}
      </ContextMenu>
    </Box>
  );
};

export default ExcelSheetTabs;
